import { Box } from "@mui/material";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useIsReadOnlyForDepartment } from "../../../hooks/useIsReadOnlyForDepartment";
import { useTeamRoster } from "../../../hooks/useTeamRoster";
import { CONTENT_WIDTH } from "../../../theme/tokens";
import { JumpingDots } from "../../UIToolkit/Animations/JumpingDots";
import type { HolidayStaffFilter } from "../teamHolidays";
import { HolidayDayPartHoursSection } from "./HolidayDayPartHoursSection";
import { HolidayGroupBalanceSection } from "./HolidayGroupBalanceSection";
import { HolidayGroupChangeDialog } from "./HolidayGroupChangeDialog";
import { HolidayGroupsHero } from "./HolidayGroupsHero";
import { HolidayListSection } from "./HolidayListSection";
import { HolidayNotAssignedSection } from "./HolidayNotAssignedSection";
import { HolidaySectionError } from "./HolidaySectionError";
import type { HolidayGroupChangePerson } from "./holidayApi";
import { useActiveHolidayPeople, useHolidaySettings } from "./holidayQueries";

type Props = {
	/** Opens the Team roster on the people behind a Group balance number, or on everyone not assigned. */
	onShowOnTeam: (filter: HolidayStaffFilter) => void;
};

/**
 * Team → Holidays: the department's holiday groups, its holidays and who works each one, the hours of each
 * day part, how evenly the groups cover them, and who has no group yet. Choosing groups here doesn't change
 * the schedule.
 */
export const HolidaysTab: FC<Props> = ({ onShowOnTeam }) => {
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	const { track } = useAnalytics();
	const canEdit = !useIsReadOnlyForDepartment();
	const settings = useHolidaySettings(departmentId);
	const people = useActiveHolidayPeople(departmentId);
	const rosterQuery = useTeamRoster(departmentId);
	// Not assigned and Group balance read names and jobs from the roster: they wait for it, and say so if it fails.
	const roster = rosterQuery.isLoading || rosterQuery.isError ? undefined : rosterQuery.roster;
	const sectionsError = people.isError || rosterQuery.isError;
	const [groupChange, setGroupChange] = useState<{ person: HolidayGroupChangePerson; groupId: string | null } | null>(null);
	const notAssignedRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		track("team_holidays_tab_viewed", { department_id: departmentId });
	}, [track, departmentId]);

	const handleChangeGroup = useCallback((person: HolidayGroupChangePerson, groupId: string | null) => setGroupChange({ person, groupId }), []);
	const showNotAssigned = useCallback(() => notAssignedRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), []);
	const { refetch: refetchPeople, isError: peopleError } = people;
	const { refetch: refetchRoster, isError: rosterError } = rosterQuery;
	const retrySections = useCallback(() => {
		if (peopleError) void refetchPeople();
		if (rosterError) void refetchRoster();
	}, [peopleError, refetchPeople, rosterError, refetchRoster]);

	if (settings.isError) {
		return (
			<Box sx={{ width: "100%", maxWidth: CONTENT_WIDTH.WIDE, mx: "auto", py: 2 }}>
				<HolidaySectionError onRetry={() => settings.refetch()} dataTestId="holidays-tab-error" />
			</Box>
		);
	}
	if (settings.data === undefined) {
		return (
			<Box sx={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center", py: 6 }} data-testid="holidays-tab-loading">
				<JumpingDots size={6} />
			</Box>
		);
	}
	const { groups, jobs } = settings.data;

	return (
		<Box
			data-testid="holidays-tab"
			sx={{ width: "100%", maxWidth: CONTENT_WIDTH.WIDE, mx: "auto", display: "flex", flexDirection: "column", gap: 3, py: 2, pb: 6 }}
		>
			<HolidayGroupsHero groups={groups} people={people.data} canEdit={canEdit} onShowNotAssigned={showNotAssigned} />
			<HolidayListSection groups={groups} canEdit={canEdit} />
			<HolidayDayPartHoursSection jobs={jobs} canEdit={canEdit} />
			<HolidayGroupBalanceSection
				groups={groups}
				people={people.data}
				isError={sectionsError}
				onRetry={retrySections}
				roster={roster}
				onShowOnTeam={onShowOnTeam}
			/>
			<HolidayNotAssignedSection
				ref={notAssignedRef}
				groups={groups}
				people={people.data}
				isError={sectionsError}
				onRetry={retrySections}
				roster={roster}
				canEdit={canEdit}
				onChangeGroup={handleChangeGroup}
				onShowOnTeam={onShowOnTeam}
			/>
			{groupChange && (
				<HolidayGroupChangeDialog people={[groupChange.person]} groups={groups} targetGroupId={groupChange.groupId} onClose={() => setGroupChange(null)} />
			)}
		</Box>
	);
};
